import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '../../../../../lib/supabase-auth'
import { useAuth } from '../../../../../contexts/AuthContextLazy'
import { 
  ArrowLeftIcon,
  BuildingOffice2Icon,
  LockClosedIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline'

export const MyMeetingsConnect: React.FC = () => {
  const navigate = useNavigate()
  const { hasRole, user } = useAuth()
  const [connecting, setConnecting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [authCode, setAuthCode] = useState<string | null>(null)

  useEffect(() => {
    // Check for OAuth callback
    const urlParams = new URLSearchParams(window.location.search)
    const code = urlParams.get('code')
    const state = urlParams.get('state')
    const error = urlParams.get('error')

    if (error) {
      setError(`Authentication failed: ${error}`)
      return
    }

    if (code && state) {
      handleOAuthCallback(code, state)
    }
  }, [])

  const handleOAuthCallback = async (code: string, state: string) => {
    setConnecting(true)
    setError(null)

    try {
      // In production, this would:
      // 1. Verify the state parameter matches what we sent
      // 2. Exchange the code for access/refresh tokens
      // 3. Store the encrypted tokens in the database
      
      // For demo purposes, simulate the token exchange
      await new Promise(resolve => setTimeout(resolve, 2000))

      // Store the integration credentials
      const { error: dbError } = await supabase
        .from('integration_credentials')
        .insert({
          integration_type: 'mymeetings',
          credentials: {
            access_token: 'demo_access_token', // Would be encrypted
            refresh_token: 'demo_refresh_token', // Would be encrypted
            mymeetings_org_id: 'pcm-financial',
            mymeetings_org_name: 'PCM Financial',
            scopes: ['read:meetings', 'read:clients', 'read:summaries'],
            expires_at: new Date(Date.now() + 3600000).toISOString() // 1 hour
          },
          sync_settings: {
            frequency: 'hourly',
            auto_sync: true,
            sync_meetings: true,
            sync_action_items: true,
            sync_client_updates: true
          },
          created_by: user?.id
        })

      if (dbError) throw dbError

      // Log the successful connection
      await supabase
        .from('integration_sync_logs')
        .insert({
          integration_type: 'mymeetings',
          action: 'connection_established',
          details: 'MyMeetings account successfully connected',
          status: 'success',
          user_id: user?.id
        })

      // Redirect to the integration management page
      navigate('/portal/admin/integrations/mymeetings')
    } catch (err: any) {
      setError(err.message || 'Failed to connect to MyMeetings')
    } finally {
      setConnecting(false)
    }
  }

  const initiateOAuth = () => {
    // In production, this would redirect to the actual MyMeetings OAuth URL
    // For demo, we'll simulate the OAuth flow
    setConnecting(true)
    
    // Simulate redirect to MyMeetings
    setTimeout(() => {
      // Simulate successful OAuth callback
      const mockCode = 'demo_auth_code_' + Date.now()
      const mockState = 'secure_state_token'
      
      // Simulate the redirect back with auth code
      window.history.pushState({}, '', `?code=${mockCode}&state=${mockState}`)
      handleOAuthCallback(mockCode, mockState)
    }, 1500)
  }

  if (!hasRole(['super_admin', 'pcm_staff'])) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">You don't have permission to view this page.</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/portal/admin/integrations"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to Integrations
          </Link>
          <h1 className="mt-4 text-3xl font-bold text-gray-900">Connect MyMeetings</h1>
          <p className="mt-2 text-base text-gray-600">
            Securely connect your MyMeetings account to sync client data
          </p>
        </div>

        <div className="bg-white portal-content-shadow-sm rounded-lg border border-gray-200">
          <div className="p-8">
            {/* MyMeetings Logo/Branding */}
            <div className="text-center mb-8">
              <img 
                src="/assets/integrations/mymeetings-logo.png" 
                alt="MyMeetings logo"
                className="mx-auto h-16 w-auto"
              />
              <h2 className="mt-4 text-xl font-semibold text-gray-900">MyMeetings Integration</h2>
              <p className="mt-2 text-sm text-gray-600">
                Connect your MyMeetings account to enable powerful features
              </p>
            </div>

            {/* Features List */}
            <div className="mb-8">
              <h3 className="text-sm font-medium text-gray-900 mb-4">What you'll get:</h3>
              <div className="space-y-3">
                <div className="flex items-start">
                  <UserGroupIcon className="h-5 w-5 text-green-500 mt-0.5" />
                  <div className="ml-3">
                    <p className="text-sm text-gray-900">Automatic client profile syncing</p>
                    <p className="text-xs text-gray-500">Keep client information up-to-date across both platforms</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <DocumentTextIcon className="h-5 w-5 text-green-500 mt-0.5" />
                  <div className="ml-3">
                    <p className="text-sm text-gray-900">Meeting summary access</p>
                    <p className="text-xs text-gray-500">View AI-generated meeting summaries in the client portal</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <ClipboardDocumentListIcon className="h-5 w-5 text-green-500 mt-0.5" />
                  <div className="ml-3">
                    <p className="text-sm text-gray-900">Action item tracking</p>
                    <p className="text-xs text-gray-500">See and manage action items from client meetings</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Security Notice */}
            <div className="mb-8 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex">
                <ShieldCheckIcon className="h-5 w-5 text-amber-400" />
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-amber-800">Security & Privacy</h4>
                  <div className="mt-1 text-sm text-amber-700">
                    <ul className="list-disc list-inside space-y-1">
                      <li>Your credentials are encrypted and stored securely</li>
                      <li>We only access data you explicitly authorize</li>
                      <li>You can disconnect at any time</li>
                      <li>Only PCM Financial organization data will be synced</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                  <div className="ml-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Connect Button */}
            <div className="text-center">
              {connecting ? (
                <div className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-gray-700 bg-gray-100">
                  <ArrowPathIcon className="mr-3 h-5 w-5 animate-spin" />
                  Connecting to MyMeetings...
                </div>
              ) : (
                <button
                  onClick={initiateOAuth}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <LockClosedIcon className="mr-3 h-5 w-5" />
                  Connect with MyMeetings
                </button>
              )}
            </div>

            {/* Help Text */}
            <p className="mt-4 text-center text-xs text-gray-500">
              You'll be redirected to MyMeetings to authorize the connection.
              Make sure you're logged in with your PCM Financial MyMeetings account.
            </p>
          </div>
        </div>

        {/* Additional Information */}
        <div className="mt-8 bg-gray-50 rounded-lg p-6">
          <h3 className="text-sm font-medium text-gray-900 mb-3">How it works:</h3>
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
            <li>Click "Connect with MyMeetings" above</li>
            <li>Log in to your MyMeetings account (if not already logged in)</li>
            <li>Authorize PCM Portal to access your organization's data</li>
            <li>You'll be redirected back here automatically</li>
            <li>Start using the integrated features immediately</li>
          </ol>
        </div>
      </div>
    </div>
  )
}